import { useEffect, useState } from 'react';

import type { YouTubeApiStatus, YouTubeGlobal } from './player.types';

const SCRIPT_SELECTOR = 'script[data-kudu-youtube-api="true"]';
const SCRIPT_SRC = 'https://www.youtube.com/iframe_api';

let youTubeApiPromise: Promise<YouTubeGlobal> | null = null;

function getYouTubeGlobal() {
  return window.YT?.Player ? window.YT : null;
}

function loadYouTubeApi(): Promise<YouTubeGlobal> {
  const readyApi = getYouTubeGlobal();

  if (readyApi) {
    return Promise.resolve(readyApi);
  }

  if (youTubeApiPromise) {
    return youTubeApiPromise;
  }

  youTubeApiPromise = new Promise<YouTubeGlobal>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(SCRIPT_SELECTOR);
    const script = existingScript ?? document.createElement('script');
    const previousReady = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      previousReady?.();

      const api = getYouTubeGlobal();

      if (!api) {
        reject(new Error('YouTube API loaded without exposing window.YT.Player.'));
        return;
      }

      resolve(api);
    };

    if (!existingScript) {
      script.async = true;
      script.dataset.kuduYoutubeApi = 'true';
      script.src = SCRIPT_SRC;
      script.addEventListener('error', () => {
        reject(new Error('Unable to load the YouTube IFrame API.'));
      });
      document.head.append(script);
    }
  }).catch((error) => {
    youTubeApiPromise = null;
    throw error;
  });

  return youTubeApiPromise;
}

export function resetYouTubeApiForTests() {
  youTubeApiPromise = null;
  const script = document.querySelector<HTMLScriptElement>(SCRIPT_SELECTOR);
  script?.remove();
  delete window.YT;
  delete window.onYouTubeIframeAPIReady;
}

export function useYouTubeApi() {
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [status, setStatus] = useState<YouTubeApiStatus>(() =>
    getYouTubeGlobal() ? 'ready' : 'idle',
  );

  useEffect(() => {
    let isMounted = true;

    if (getYouTubeGlobal()) {
      setStatus('ready');
      return () => {
        isMounted = false;
      };
    }

    setStatus('loading');
    loadYouTubeApi()
      .then(() => {
        if (!isMounted) {
          return;
        }

        setStatus('ready');
        setErrorMessage(undefined);
      })
      .catch((error: unknown) => {
        if (!isMounted) {
          return;
        }

        setStatus('error');
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Unable to load the YouTube IFrame API.',
        );
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return {
    errorMessage,
    isReady: status === 'ready',
    status,
  };
}
